import * as React from 'react';
import { cn } from '@/lib/utils';
import { cardVariants, type CardVariant } from '@/design-system/tokens/cardVariants';

/**
 * AppCard \u2014 Standardized Card Component (MANDATORY)
 *
 * Uses the global `.card` CSS class from the clean design system.
 * - rounded-xl border, bg-card, shadow-sm, hover:shadow-md
 * - p-4 default padding
 *
 * Replaces inconsistent Card usage across pages.
 * All sections/panels MUST use AppCard for visual consistency.
 *
 * Usage (Compositional - Preferred):
 *   <AppCard>
 *     <AppCardHeader>
 *       <AppCardTitle>Section Title</AppCardTitle>
 *       <AppCardDescription>Optional description</AppCardDescription>
 *     </AppCardHeader>
 *     <AppCardContent>
 *       Content here
 *     </AppCardContent>
 *   </AppCard>
 *
 * Usage (Convenience Props - Backward Compatible):
 *   <AppCard title="Section Title" description="Optional description">
 *     Content here
 *   </AppCard>
 */

export interface AppCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Remove default padding */
  noPadding?: boolean;
  /** Remove border */
  noBorder?: boolean;
  /** Make card interactive (hover effects) */
  interactive?: boolean;
  /** Semantic card variant from design system token */
  variant?: CardVariant;
  /** Convenience prop: Card title (renders AppCardHeader with AppCardTitle) */
  title?: string;
  /** Convenience prop: Card description (renders AppCardDescription under title) */
  description?: string;
  /** Convenience prop: Header actions (right side) */
  headerActions?: React.ReactNode;
}

const AppCard = React.forwardRef<HTMLDivElement, AppCardProps>(
  ({ className, noPadding, noBorder, interactive, variant, title, description, headerActions, children, ...props }, ref) => {
    const hasConvenienceHeader = title || description || headerActions;
    
    return (
      <div
        ref={ref}
        className={cn(
          variant ? cardVariants[variant] : 'card',
          noBorder && 'border-0',
          !noPadding && !hasConvenienceHeader && 'card-padding',
          interactive && 'hover:-translate-y-[1px] cursor-pointer',
          className
        )}
        {...props}
      >
        {hasConvenienceHeader ? (
          <>
            {/* Convenience header with border */}
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
              <div className="min-w-0">
                {title && <h3 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h3>}
                {description && <p className="text-[12px] text-muted-foreground mt-0.5">{description}</p>}
              </div>
              {headerActions && (
                <div className="flex items-center gap-2 flex-shrink-0">{headerActions}</div>
              )}
            </div>
            {/* Content area */}
            <div className={cn(!noPadding && 'p-4')}>{children}</div>
          </>
        ) : (
          children
        )}
      </div>
    );
  }
);
AppCard.displayName = 'AppCard';

export interface AppCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Actions to render on the right side */
  actions?: React.ReactNode;
}

const AppCardHeader = React.forwardRef<HTMLDivElement, AppCardHeaderProps>(
  ({ className, actions, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 pb-3',
        className
      )}
      {...props}
    >
      <div className="flex flex-col space-y-1 min-w-0">{children}</div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0">{actions}</div>
      )}
    </div>
  )
);
AppCardHeader.displayName = 'AppCardHeader';

const AppCardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-[14px] font-semibold leading-tight tracking-tight text-foreground',
      className
    )}
    {...props}
  />
));
AppCardTitle.displayName = 'AppCardTitle';

const AppCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-[12px] text-muted-foreground', className)}
    {...props}
  />
));
AppCardDescription.displayName = 'AppCardDescription';

const AppCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('space-y-3', className)} {...props} />
));
AppCardContent.displayName = 'AppCardContent';

const AppCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2 pt-3 border-t border-border',
      className
    )}
    {...props}
  />
));
AppCardFooter.displayName = 'AppCardFooter';

/**
 * AppCardSection – Divider within a card for grouped content
 */
const AppCardSection = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { title?: string }
>(({ className, title, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('pt-3 border-t border-border first:pt-0 first:border-t-0', className)}
    {...props}
  >
    {title && (
      <h4 className="text-[12px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
        {title}
      </h4>
    )}
    {children}
  </div>
));
AppCardSection.displayName = 'AppCardSection';

export {
  AppCard,
  AppCardHeader,
  AppCardTitle,
  AppCardDescription,
  AppCardContent,
  AppCardFooter,
  AppCardSection,
};
export default AppCard;

